import {
  formatCustomerOrderTaxTypeLabel,
} from '#/modules/crmMgmt/customerOrder/schemas/customerOrderSchema';
import {
  formatDateOrDash,
  formatDateTimeOrDash,
} from '#/utils/date';
import { formatDocumentCurrencyLabel } from '#/utils/documentCurrencyLabel';
import { apiDiscountRateToMultiplier } from '#/utils/discountRate';
import { formatDisplayAmount } from '#/utils/formatNumber';

export function displayOrDash(value) {
  if (value == null || value === '') {
    return '—';
  }
  return value;
}

/** Detail line／manualLines 數量物件；缺則 null（再 fallback 扁平欄）。 */
export function getCustomerOrderLineQtySummary(line) {
  return line?.qtySummary ?? null;
}

function pickLineQty(line, summaryKey, ...flatKeys) {
  const fromSummary = getCustomerOrderLineQtySummary(line)?.[summaryKey];
  if (fromSummary != null && fromSummary !== '') {
    return fromSummary;
  }
  for (const key of flatKeys) {
    const value = line?.[key];
    if (value != null && value !== '') {
      return value;
    }
  }
  return undefined;
}

export function getCustomerOrderLineOrderQty(line) {
  return pickLineQty(line, 'orderQty', 'orderQty');
}

export function getCustomerOrderLineCancelQty(line) {
  return pickLineQty(line, 'cancelQty', 'cancelQty');
}

/**
 * 已開銷貨。權威 `qtySummary.salesOrderQty`；fallback 扁平／舊 `shippedQty`。
 */
export function getCustomerOrderLineSalesOrderQty(line) {
  return pickLineQty(line, 'salesOrderQty', 'salesOrderQty', 'shippedQty');
}

/**
 * 可開銷貨。權威 `qtySummary.remainingSalesOrderQty`；fallback 扁平／舊 `remainingQty`。
 */
export function getCustomerOrderLineRemainingQty(line) {
  return pickLineQty(
    line,
    'remainingSalesOrderQty',
    'remainingSalesOrderQty',
    'remainingQty',
  );
}

/**
 * 已送達。權威 `qtySummary.deliveredQty`；fallback 扁平／舊 `completedQty`。
 */
export function getCustomerOrderLineDeliveredQty(line) {
  return pickLineQty(line, 'deliveredQty', 'deliveredQty', 'completedQty');
}

/**
 * 未送達。權威 `qtySummary.remainingDeliveryQty`；fallback 扁平。
 */
export function getCustomerOrderLineRemainingDeliveryQty(line) {
  return pickLineQty(line, 'remainingDeliveryQty', 'remainingDeliveryQty');
}

/** Detail 用料快照：`mtrRequirements` 優先，舊 `materials` fallback。 */
export function getCustomerOrderLineMtrRequirements(line) {
  if (Array.isArray(line?.mtrRequirements)) {
    return line.mtrRequirements;
  }
  if (Array.isArray(line?.materials)) {
    return line.materials;
  }
  return [];
}

export function getMaterialSnapshotMtr(material) {
  return material?.mtr ?? null;
}

export function getMaterialSnapshotMtrId(material) {
  return getMaterialSnapshotMtr(material)?.id
    ?? material?.mtrId
    ?? material?.mtr_id
    ?? '';
}

export function getMaterialSnapshotMtrNo(material) {
  return getMaterialSnapshotMtr(material)?.mtrNo
    ?? material?.mtrNo
    ?? material?.mtr_no
    ?? '';
}

export function getMaterialSnapshotMtrName(material) {
  return getMaterialSnapshotMtr(material)?.name
    ?? material?.mtrName
    ?? material?.mtr_name
    ?? '';
}

export function getMaterialSnapshotQtySummary(material) {
  return material?.qtySummary ?? null;
}

export function getMaterialSnapshotQtyPerProduct(material) {
  const fromSummary = getMaterialSnapshotQtySummary(material)?.qtyPerProduct;
  if (fromSummary != null && fromSummary !== '') {
    return fromSummary;
  }
  return material?.qtyPerProduct ?? material?.qty_per_product;
}

export function getMaterialSnapshotBaseUnitName(material) {
  const unit = material?.baseUnit;
  if (unit) {
    return unit.name ?? unit.code ?? '';
  }
  return material?.baseUnitName ?? material?.baseUnitCode ?? '';
}

export function getMaterialSnapshotOriginalRequiredQty(material) {
  return getMaterialSnapshotQtySummary(material)?.originalRequiredQty
    ?? material?.originalRequiredQty;
}

export function getMaterialSnapshotRemainingRequiredQty(material) {
  return getMaterialSnapshotQtySummary(material)?.remainingRequiredQty
    ?? material?.remainingRequiredQty;
}

export function getMaterialSnapshotOpenedWorkOrderProductionQty(material) {
  return getMaterialSnapshotQtySummary(material)?.openedWorkOrderProductionQty
    ?? material?.openedWorkOrderProductionQty;
}

export function getMaterialSnapshotRemainingProductionQty(material) {
  return getMaterialSnapshotQtySummary(material)?.remainingProductionQty
    ?? material?.remainingProductionQty;
}

export function getExtraItemQtySummary(item) {
  return item?.qtySummary ?? null;
}

export function getExtraItemQtyPerProduct(item) {
  return getExtraItemQtySummary(item)?.qtyPerProduct ?? item?.qtyPerProduct;
}

export function getExtraItemReferenceQty(item) {
  return getExtraItemQtySummary(item)?.referenceQty ?? item?.referenceQty;
}

export function getExtraItemDeliveredQty(item) {
  return getExtraItemQtySummary(item)?.deliveredQty ?? item?.deliveredQty;
}

export function getExtraItemRemainingReferenceQty(item) {
  return getExtraItemQtySummary(item)?.remainingReferenceQty
    ?? item?.remainingReferenceQty;
}

/** Detail line／manualLines 金額物件（禁讀舊 line.amountSummary／頂層扁平小計）。 */
export function getCustomerOrderLineAmount(line) {
  return line?.amount ?? null;
}

export function getCustomerOrderLineUnitPrice(line) {
  return getCustomerOrderLineAmount(line)?.unitPrice;
}

/** 畫面折數（價格倍率，0.9＝九折）；後端 `discount_rate` 是折抵比例，於此轉換。 */
export function getCustomerOrderLineDiscountRate(line) {
  return apiDiscountRateToMultiplier(getCustomerOrderLineAmount(line)?.discountRate);
}

/** 每件固定折扣（`amount.discountAmount`；與折數二擇一輸入，不是整列折抵）。 */
export function getCustomerOrderLineUnitDiscountAmount(line) {
  return getCustomerOrderLineAmount(line)?.discountAmount;
}

/** 整列折抵：數量 × 單價 × 後端 discount_rate（折抵比例），或每件固定折扣 × 數量。 */
export function getCustomerOrderLineDiscountTotalAmount(line) {
  return getCustomerOrderLineAmount(line)?.discountTotalAmount;
}

/** 逐列取整差額（手填列歷史快照為已存 net − gross）。 */
export function getCustomerOrderLineRoundingAdjustment(line) {
  return getCustomerOrderLineAmount(line)?.roundingAdjustment;
}

/** 列小計：`amount.subtotalAmount`（或 `netAmount`）。 */
export function getCustomerOrderLineSubtotalAmount(line) {
  const amount = getCustomerOrderLineAmount(line);
  if (!amount) {
    return null;
  }
  if (amount.subtotalAmount != null && amount.subtotalAmount !== '') {
    return amount.subtotalAmount;
  }
  return amount.netAmount ?? null;
}

function formatCustomerLabel(customer) {
  if (!customer) {
    return '—';
  }
  const label = [customer.code, customer.name].filter(Boolean).join(' - ');
  return label || '—';
}

function formatTaxRateDisplay(taxRate) {
  if (taxRate == null || taxRate === '') {
    return '—';
  }
  const n = Number(taxRate);
  if (!Number.isFinite(n)) {
    return String(taxRate);
  }
  return String(n);
}

/**
 * 客戶訂單 MainInfoCard／金額區 meta。
 * Header 金額只讀 `amountSummary`（Search 無則 hasAmount=false；禁頂層扁平權威）。
 */
export function mapCustomerOrderMeta(detail) {
  const amountSummary = detail?.amountSummary ?? null;
  const hasAmount = Boolean(amountSummary)
    && (
      amountSummary.subtotalAmount != null
      || amountSummary.discountAmount != null
      || amountSummary.netAmount != null
      || amountSummary.taxAmount != null
      || amountSummary.roundingAdjustment != null
      || amountSummary.totalAmount != null
    );

  return {
    code: detail?.customerOrderNo || '-',
    status: detail?.status || '',
    customerSubtitle: formatCustomerLabel(detail?.customer),
    createDate: formatDateTimeOrDash(detail?.record?.createdAt),
    reviseDate: formatDateTimeOrDash(detail?.record?.updatedAt),
    closeDate: formatDateTimeOrDash(detail?.history?.closedAt),
    terminatedDate: formatDateTimeOrDash(detail?.history?.terminatedAt),
    submittedDate: formatDateTimeOrDash(detail?.history?.submittedAt),
    approvedDate: formatDateTimeOrDash(detail?.history?.approvedAt),
    rejectedDate: formatDateTimeOrDash(detail?.history?.rejectedAt),
    rejectReason: detail?.history?.rejectReason ?? '',
    closeReason: detail?.history?.closeReason ?? '',
    terminateReason: detail?.history?.terminateReason ?? '',
    customerOrderDate: formatDateOrDash(detail?.customerOrderDate),
    currencyCode: formatDocumentCurrencyLabel(detail),
    taxType: formatCustomerOrderTaxTypeLabel(detail?.taxType),
    taxRate: formatTaxRateDisplay(detail?.taxRate),
    hasAmount,
    subtotalAmount: formatDisplayAmount(amountSummary?.subtotalAmount),
    discountAmount: formatDisplayAmount(amountSummary?.discountAmount),
    netAmount: formatDisplayAmount(amountSummary?.netAmount),
    taxAmount: formatDisplayAmount(amountSummary?.taxAmount),
    roundingAdjustment: formatDisplayAmount(amountSummary?.roundingAdjustment),
    totalAmount: formatDisplayAmount(amountSummary?.totalAmount),
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    billingAddressId: detail?.billingAddressId ?? '',
    shippingAddressId: detail?.shippingAddressId ?? '',
    shippingContactId: detail?.shippingContactId ?? '',
  };
}
